import type { ReactElement, ReactNode } from 'react'
import {
  Calculator,
  CheckCircle2,
  Clock,
  CloudOff,
  MapPin,
  RefreshCw,
  Sparkles,
  TriangleAlert,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { RecommendationSummary } from '@/components/custom/deal-review/review-controls'
import {
  dispositionLabel,
  PROCESSING_STAGES,
  stageProgress,
} from '@/features/deal-review/deal-lead-labels'
import { ratio } from '@/features/deal-review/deal-lead-fields'
import {
  displayLabelFor,
  displayNameFor,
  isInProgress,
  processingStatusFor,
  sendFailureMessage,
  statusFor,
  syncStatusFor,
  type DealLeadView,
} from '@/features/deal-review/deal-lead-views'
import { MUTED_TEXT } from '@/features/deal-review/review-tones'
import { useIsClamped } from '@/hooks/use-is-clamped'
import { AddNamePopover } from '@/components/custom/deal-review/deal-lead-naming'
import type { LeadSelection } from '@/components/custom/deal-review/selection-bar'
import { STATUS_TONE_CLASSES } from '@/features/deal-review/review-tones'
import { formatMillions } from '@/features/deal-review/deal-lead-fields'
import { useCachedDealLeads } from '@/hooks/use-deal-leads'
import type { DealCloudSyncState, DealLeadListItem } from '@/types/api'

// Row clicks open the peek; Radix portals open menus outside this subtree, so only triggers need this.
export function OwnClick({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={className}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {children}
    </div>
  )
}

// Written out, not interpolated: Tailwind only emits classes it can read as literals.
const CLAMP_CLASSES: Record<1 | 2 | 3, string> = {
  1: 'block truncate',
  2: 'line-clamp-2 whitespace-normal',
  3: 'line-clamp-3 whitespace-normal',
}

export function TruncatedCell({
  value,
  lines,
  className = 'text-primary/75',
}: {
  value: string | undefined
  lines: 1 | 2 | 3
  className?: string
}) {
  const [measure, clamped] = useIsClamped()

  if (!value) {
    return (
      <Typography as="span" variant="body-sm" className={MUTED_TEXT}>
        —
      </Typography>
    )
  }

  return (
    <RevealWhenClamped clamped={clamped} text={value}>
      <span
        // Remounted on a new value so the clamp is measured again.
        key={value}
        ref={measure}
        tabIndex={clamped ? 0 : undefined}
        className={cn(
          'rounded-sm text-xs leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
          CLAMP_CLASSES[lines]
        )}
      >
        {value}
      </span>
    </RevealWhenClamped>
  )
}

export function SubSectorCell({ lead }: { lead: DealLeadListItem }) {
  if (!lead.subSector) {
    return (
      <Typography as="span" variant="body-sm" className={MUTED_TEXT}>
        —
      </Typography>
    )
  }

  return (
    <Typography
      as="span"
      variant="body-sm"
      className="whitespace-normal break-words leading-5 text-primary/75"
    >
      {lead.subSector}
    </Typography>
  )
}

function HintTrigger({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon
  label: string
  children: ReactNode
}) {
  return (
    <OwnClick>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            className="shrink-0 rounded-sm p-1 text-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Icon aria-hidden="true" className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-72">{children}</TooltipContent>
      </Tooltip>
    </OwnClick>
  )
}

export function RowProgress({ lead }: { lead: DealLeadListItem }) {
  const { label, done } = stageProgress(lead.completedStages ?? [])
  const total = PROCESSING_STAGES.length

  return (
    <div className="flex h-full items-center">
      <div className="w-full max-w-sm space-y-1.5 text-sky-900">
        <div className="flex items-baseline gap-2">
          <span className="relative flex size-3 shrink-0 self-center">
            <span className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-sky-600" />
          </span>

          <Typography
            as="span"
            variant="body-sm"
            className="truncate font-medium"
            title={label}
          >
            {label}
          </Typography>

          <Typography
            as="span"
            variant="body-sm"
            className="ml-auto shrink-0 whitespace-nowrap text-xs tabular-nums text-sky-900/50"
          >
            {done + 1} of {total}
          </Typography>
        </div>

        <div
          className="flex gap-1"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={done}
          aria-label={label}
        >
          {PROCESSING_STAGES.map((stage, index) => (
            <span
              key={stage.id}
              className={cn(
                'h-1 flex-1 rounded-full transition-colors duration-500',
                index < done && 'bg-sky-600/60',
                index === done && 'animate-pulse bg-sky-600',
                index > done && 'bg-sky-600/12'
              )}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export function MoneyCell({ value }: { value: number | undefined }) {
  if (value === undefined) {
    return (
      <Typography as="span" variant="body-sm" className={MUTED_TEXT}>
        —
      </Typography>
    )
  }

  return (
    <Typography as="span" variant="body-sm" className="tabular-nums">
      {formatMillions(value)}
    </Typography>
  )
}

export function MarginCell({ lead }: { lead: DealLeadListItem }) {
  const value = ratio(lead.ebitdaMarginPct)
  if (value === null) {
    return (
      <Typography as="span" variant="body-sm" className={MUTED_TEXT}>
        —
      </Typography>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <Typography as="span" variant="body-sm" className="tabular-nums">
        {value}
      </Typography>
      {lead.ebitdaMarginDerived && (
        <HintTrigger icon={Calculator} label="Computed margin">
          <Typography variant="body-sm" className="whitespace-normal">
            Computed from revenue and EBITDA. The stated margin contradicted
            them.
          </Typography>
        </HintTrigger>
      )}
    </div>
  )
}

export function HeadquartersCell({ lead }: { lead: DealLeadListItem }) {
  if (!lead.geography) {
    return (
      <Typography as="span" variant="body-sm" className={MUTED_TEXT}>
        —
      </Typography>
    )
  }

  return (
    <div className="flex gap-1.5">
      <MapPin className="mt-0.5 size-3.5 shrink-0 text-secondary" />
      <Typography
        as="span"
        variant="body-sm"
        className="whitespace-normal break-words leading-5 text-primary/75"
      >
        {lead.geography}
      </Typography>
    </div>
  )
}

export function SourceCell({ lead }: { lead: DealLeadListItem }) {
  return (
    <Typography
      as="span"
      variant="body-sm"
      className="whitespace-normal break-words font-medium leading-5 text-primary/75"
    >
      {lead.brokerCompany ??
        lead.senderName ??
        lead.senderEmail ??
        'Unknown sender'}
    </Typography>
  )
}

export function ReceivedCell({ lead }: { lead: DealLeadListItem }) {
  return (
    <Typography
      as="span"
      variant="body-sm"
      className="whitespace-nowrap tabular-nums text-primary/75"
    >
      {new Date(lead.receivedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })}
    </Typography>
  )
}

export function RecommendationHint({
  lead,
  view,
}: {
  lead: DealLeadListItem
  view: DealLeadView
}) {
  const recommended = lead.recommendedDisposition
  if (!recommended || recommended === 'UNREVIEWED') return null

  if (lead.disposition === 'UNREVIEWED') return null
  if (lead.disposition === recommended) return null

  const verb = view === 'history' ? 'recommended' : 'recommends'
  const summary = `Screening ${verb} ${dispositionLabel(recommended)}`

  return (
    <HintTrigger icon={Sparkles} label={summary}>
      <RecommendationSummary
        summary={summary}
        reason={lead.recommendationReason}
      />
    </HintTrigger>
  )
}

export function StatusCell({ lead }: { lead: DealLeadListItem }) {
  const status = statusFor(lead)

  return (
    <Badge variant="outline" className={STATUS_TONE_CLASSES[status.tone]}>
      {status.label}
    </Badge>
  )
}

const SYNC_ICONS: Partial<Record<DealCloudSyncState, LucideIcon>> = {
  QUEUED: Clock,
  SENDING: RefreshCw,
  SENT: CheckCircle2,
  FAILED: TriangleAlert,
}

function FailureReason({
  lead,
  children,
}: {
  lead: DealLeadListItem
  children: ReactElement
}) {
  // The list carries no failure text; a detail fetched this session does.
  const [detail] = useCachedDealLeads([lead.id])
  const reason = detail
    ? sendFailureMessage(detail)
    : 'DealCloud did not accept this lead.'
  const reasonId = `dealcloud-failure-${lead.id}`

  return (
    <OwnClick>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            aria-describedby={reasonId}
            className="inline-flex"
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-64">{reason}</TooltipContent>
      </Tooltip>
      <span id={reasonId} className="sr-only">
        {reason}
      </span>
    </OwnClick>
  )
}

// A decided lead waiting to be sent is the common case, so it gets a quiet icon, not a badge.
export function NotSentMark({ lead }: { lead: DealLeadListItem }) {
  const sync = syncStatusFor(lead)
  if (!sync || lead.dealCloudSyncState !== 'NOT_SENT') return null

  return (
    <OwnClick>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            tabIndex={0}
            aria-label={sync.label}
            className="inline-flex shrink-0 rounded-sm p-1 text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CloudOff aria-hidden="true" className="size-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent>{sync.label}</TooltipContent>
      </Tooltip>
    </OwnClick>
  )
}

export function DealCloudStateBadge({ lead }: { lead: DealLeadListItem }) {
  const sync = syncStatusFor(lead)
  if (!sync || lead.dealCloudSyncState === 'NOT_SENT') return null

  const Icon = SYNC_ICONS[lead.dealCloudSyncState]
  const badge = (
    <Badge
      variant="outline"
      className={cn(
        'h-5 gap-1 px-1.5 text-xs font-normal',
        STATUS_TONE_CLASSES[sync.tone]
      )}
    >
      {Icon && (
        <Icon
          aria-hidden="true"
          className={cn(
            'size-3',
            lead.dealCloudSyncState === 'SENDING' && 'motion-safe:animate-spin'
          )}
        />
      )}
      {sync.label}
    </Badge>
  )

  return lead.dealCloudSyncState === 'FAILED' ? (
    <FailureReason lead={lead}>{badge}</FailureReason>
  ) : (
    badge
  )
}

// DataTable's onRowClick only reaches the `displayName` column, so the link is wired here.
export function CompanyCell({
  lead,
  onSelect,
}: {
  lead: DealLeadListItem
  onSelect: (leadId: string) => void
}) {
  const label = displayLabelFor(lead)

  const status = processingStatusFor(lead)
  const processing = status && status.label !== label ? status : null

  const working = isInProgress(lead)

  const placeholder = isInProgress(lead) && displayNameFor(lead) === null
  const needsName =
    !working && displayNameFor(lead) === null && lead.capabilities.canCorrect

  // Not `CellLink`, which truncates; long names must wrap.
  return (
    // `w-full`: without a width, `truncate` below has nothing to measure against.
    <div className="flex w-full flex-col items-start gap-1">
      {processing && !working && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className={STATUS_TONE_CLASSES[processing.tone]}
            >
              {processing.label}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            {lead.subject
              ? `From the email "${lead.subject}"`
              : 'From the same email as any other row showing this'}
          </TooltipContent>
        </Tooltip>
      )}
      {placeholder ? (
        <div className="w-full min-w-0">
          <Typography as="span" variant="body-sm" className="block font-medium">
            {lead.sourceFileName ?? 'Email body'}
          </Typography>
          <TruncatedCell
            value={lead.subject ?? 'Not yet extracted'}
            lines={1}
            className="text-muted-foreground"
          />
        </div>
      ) : (
        <Button
          type="button"
          variant="link"
          className="h-auto min-w-0 justify-start whitespace-normal p-0 text-left no-underline hover:no-underline"
          onClick={() => onSelect(lead.id)}
        >
          {needsName ? (
            <span className="block min-w-0">
              <Typography
                as="span"
                variant="body"
                className="block text-xs font-medium leading-[1.35] text-primary/60"
              >
                {label}
              </Typography>
              {lead.subject && (
                <Typography
                  as="span"
                  variant="body-sm"
                  className="line-clamp-1 text-xs text-primary/60"
                >
                  {lead.subject}
                </Typography>
              )}
            </span>
          ) : (
            <Typography
              as="span"
              variant="body"
              className="block text-xs font-bold leading-[1.35] text-primary"
            >
              {label}
            </Typography>
          )}
        </Button>
      )}
      {needsName && (
        <OwnClick>
          <AddNamePopover lead={lead} />
        </OwnClick>
      )}
    </div>
  )
}

export function SelectAllHeader({
  selection,
  leads,
}: {
  selection: LeadSelection
  leads: DealLeadListItem[]
}) {
  const selectedHere = leads.filter((lead) => selection.ids.has(lead.id)).length
  const checked =
    leads.length > 0 && selectedHere === leads.length
      ? true
      : selectedHere > 0
        ? 'indeterminate'
        : false

  return (
    <Checkbox
      checked={checked}
      disabled={leads.length === 0}
      aria-label={
        checked === true
          ? 'Clear selection'
          : `Select all ${leads.length} loaded deals`
      }
      onCheckedChange={(next) => {
        if (next === true) selection.selectAll(leads.map((lead) => lead.id))
        else selection.clear()
      }}
    />
  )
}

export function SelectRowCell({
  lead,
  selection,
}: {
  lead: DealLeadListItem
  selection: LeadSelection
}) {
  return (
    <Checkbox
      checked={selection.ids.has(lead.id)}
      aria-label={`Select ${displayNameFor(lead) ?? 'this lead'}`}
      onCheckedChange={() => selection.toggle(lead.id)}
    />
  )
}

// The trigger is the caller's focusable element; wrapping it would add a second tab stop.
export function RevealWhenClamped({
  clamped,
  text,
  children,
}: {
  clamped: boolean
  text: string
  children: ReactElement
}) {
  if (!clamped) return children

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="max-w-96 whitespace-normal">
        {text}
      </TooltipContent>
    </Tooltip>
  )
}
